/**
 * 场景路由链的纯逻辑：有序、可增删、可换位、可校验。没有 Vue、没有 i18n、没有网络 —— 可单测。
 *
 * 链是 `[{ provider, model }, …]`，**索引 0 是主力**；上一步抛可重试错误时 `withFallback` 才走下一步
 * （`packages/llm-router/src/fallback.js`）。所以顺序不是排版，是行为。
 *
 * **重复的 (provider, model) 合法，一律不拦**：`withFallback` 遍历的是各自独立的 `Llm` 实例，每个实例
 * 自己去 keyrouter 领 key —— `[A/m1, A/m1]` 的意思是「这把 key 挂了，换同一个池子里的另一把再试一次」。
 *
 * 校验返回的是**错误码**不是文案：这个模块不认识任何语言，文案由页面查 i18n。
 * 「`kp` 链空了」这条不变量的**唯一权威是后端配置校验**（事务里跑 `validateLlmConfig`，409 + 回滚）。
 * 页面不复述它，而是让它删不出来：`ChainEditor` 里只剩一步时删除键就是 `disabled`。
 *
 * @typedef {{ provider: string, model: string }} ChainStep
 * @typedef {ChainStep & { uid: string }} EditableStep  uid 只活在编辑器里：拖拽换位后 Vue 靠它认行。
 * @typedef {'incomplete'|'unroutable'} StepError
 */

/** 编辑器行的身份。链里允许两行完全一样，所以 key 不能由内容拼出来。 */
let uidCounter = 0

/** @returns {EditableStep} 新加的一步：还没选厂商模型，校验为 `incomplete`。 */
export function newStep() {
  uidCounter += 1
  return { uid: `step-${uidCounter}`, provider: '', model: '' }
}

/**
 * 接口返回的链 → 可编辑的链。
 * @param {ChainStep[]} chain
 * @returns {EditableStep[]}
 */
export function toEditableChain(chain) {
  return chain.map((step) => ({ ...newStep(), provider: step.provider, model: step.model }))
}

/**
 * 换位（拖拽落点、上移下移都走这一个）。越界直接抛 —— 不静默夹到边界，那会让一次错误的拖拽
 * 看起来像成功了。
 * @param {EditableStep[]} steps
 * @param {number} from
 * @param {number} to
 * @returns {EditableStep[]}
 */
export function moveStep(steps, from, to) {
  assertIndex(steps, from, 'moveStep.from')
  assertIndex(steps, to, 'moveStep.to')
  const next = [...steps]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * @param {EditableStep[]} steps
 * @param {number} index
 * @returns {EditableStep[]}
 */
export function removeStep(steps, index) {
  assertIndex(steps, index, 'removeStep.index')
  return steps.filter((_, i) => i !== index)
}

/**
 * 这一步在链里的角色。索引 0 是主力，其余按出场顺序是兜底 1 / 兜底 2 / …
 * @param {number} index
 * @returns {{ role: 'primary' }|{ role: 'fallback', order: number }}
 */
export function stepRole(index) {
  if (!Number.isInteger(index) || index < 0) throw new Error(`stepRole: index must be a non-negative integer, got ${index}`)
  return index === 0 ? { role: 'primary' } : { role: 'fallback', order: index }
}

/**
 * 可选的 (厂商, 模型) 全集：**只有启用的厂商 × 启用的模型**才可路由，后端在写事务里也是这么判的
 * （`assembly/llm-admin/modes.js` 的 `assertStepsRoutable`）。
 * @param {{ providers: Array<{ id: number, enabled: number }>, text: {
 *   provider_capabilities: Array<{ id: number, provider_id: number, code: string, enabled: number }>,
 *   models: Array<{ provider_capability_id: number, model: string, enabled: number }>
 * } }} catalog
 * @returns {ChainStep[]}
 */
export function chainOptions(catalog) {
  /** @type {ChainStep[]} */
  const options = []
  const enabledProviderIds = new Set(catalog.providers.filter(provider => provider.enabled === 1).map(provider => provider.id))
  for (const providerCapability of catalog.text.provider_capabilities) {
    if (providerCapability.enabled !== 1 || !enabledProviderIds.has(providerCapability.provider_id)) continue
    for (const model of catalog.text.models) {
      if (model.provider_capability_id !== providerCapability.id || model.enabled !== 1) continue
      options.push({ provider: providerCapability.code, model: model.model })
    }
  }
  return options
}

/** 下拉框里一个选项的值。`#` 不出现在厂商名里，模型名里的 `/` 也不受影响。 */
export function stepKey(/** @type {ChainStep} */ step) {
  return `${step.provider}#${step.model}`
}

/**
 * 下拉框的值 → 一步。认不出的值直接抛，不返回半个对象。
 * @param {string} key
 * @returns {ChainStep}
 */
export function stepFromKey(key) {
  const at = key.indexOf('#')
  if (at <= 0 || at === key.length - 1) throw new Error(`stepFromKey: not a "provider#model" key: ${key}`)
  return { provider: key.slice(0, at), model: key.slice(at + 1) }
}

/**
 * 逐步校验 + 整链校验。
 *
 * - `steps[i] = 'incomplete'`：这一行还没选。
 * - `steps[i] = 'unroutable'`：选了，但那个 (厂商, 模型) 现在不可路由了（模型或厂商被停用）。
 *   已经落库的链会出现这种行，要标出来让人换掉，而不是替他猜一个。
 * - `chain = 'emptyRequired'`：`kp` 这类 runtime 场景的链是空的 —— 保存下去每个游戏请求都会炸。
 *   编辑器删不空一条链，所以这只会出现在「库里本来就是空的」上；它是这条不变量的纯逻辑形态。
 *
 * @param {{ steps: EditableStep[], required: boolean, options: ChainStep[] }} input
 * @returns {{ steps: Record<number, StepError>, chain: 'emptyRequired'|null }}
 */
export function validateChain({ steps, required, options }) {
  const routable = new Set(options.map(stepKey))
  /** @type {Record<number, StepError>} */
  const stepErrors = {}
  steps.forEach((step, index) => {
    if (step.provider === '' || step.model === '') stepErrors[index] = 'incomplete'
    else if (!routable.has(stepKey(step))) stepErrors[index] = 'unroutable'
  })
  return {
    steps: stepErrors,
    chain: required && steps.length === 0 ? 'emptyRequired' : null,
  }
}

/**
 * 编辑器 → 提交体。**空链是合法的提交形状**：它要一路走到后端写事务，被配置断言以 409 拒掉并回滚，
 * 人才知道「`kp` 不能空」—— 在这里挡成本地错误，就是把那条不变量复制成了第二份。
 *
 * 但半选的行不许提交：那不是「用户想表达的配置」，是「还没填完」。
 * @param {EditableStep[]} steps
 * @returns {{ chain: ChainStep[] }}
 */
export function toChainSaveBody(steps) {
  const half = steps.findIndex((step) => step.provider === '' || step.model === '')
  if (half >= 0) throw new Error(`toChainSaveBody: step ${half} has no (provider, model) selected`)
  return { chain: steps.map((step) => ({ provider: step.provider, model: step.model })) }
}

/**
 * 编辑器里的链和库里的链一不一样（决定「保存」要不要亮、离开要不要拦）。顺序算数。
 * @param {EditableStep[]} steps
 * @param {ChainStep[]} saved
 */
export function isChainDirty(steps, saved) {
  if (steps.length !== saved.length) return true
  return steps.some((step, i) => step.provider !== saved[i].provider || step.model !== saved[i].model)
}

/**
 * @param {unknown[]} steps
 * @param {number} index
 * @param {string} label
 */
function assertIndex(steps, index, label) {
  if (!Number.isInteger(index) || index < 0 || index >= steps.length) {
    throw new Error(`${label}: index ${index} is out of range for a chain of ${steps.length} steps`)
  }
}
